import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, RefreshCcw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";
import { BaselineEditorButton } from "./workspaceCosts/BaselineEditorButton";
import { CostBreakdownCards } from "./workspaceCosts/CostBreakdownCards";
import {
  buildCostsCsv,
  type CostsResponse,
  type WindowKey,
} from "./workspaceCosts/types";

export function WorkspaceCostsTab() {
  const [workspaceId, setWorkspaceId] = useState<string>("");
  const [window, setWindow] = useState<WindowKey>("mtd");
  const [environment, setEnvironment] = useState<"sandbox" | "live">(getStripeEnvironment());

  const { data: workspaces, isLoading: workspacesLoading } = useQuery({
    queryKey: ["super-admin-workspaces-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, workspace_label, display_name, account_tier")
        .order("workspace_label", { ascending: true });
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ["workspace-costs", workspaceId, window, environment],
    enabled: !!workspaceId,
    queryFn: async (): Promise<CostsResponse> => {
      const { data, error } = await supabase.functions.invoke("admin-workspace-costs", {
        body: { workspace_id: workspaceId, window, environment },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      return data as CostsResponse;
    },
  });

  const csv = useMemo(() => (data ? buildCostsCsv(data) : ""), [data]);

  const downloadCsv = () => {
    if (!csv || !data) return;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safeLabel = (data.workspace.workspace_label ?? "workspace")
      .replace(/[^a-z0-9]+/gi, "-")
      .toLowerCase();
    a.download = `workspace-costs-${safeLabel}-${data.window}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Workspace Cost Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[280px] flex-1">
              <label className="text-xs text-muted-foreground">Workspace</label>
              <Select
                value={workspaceId}
                onValueChange={setWorkspaceId}
                disabled={workspacesLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder={workspacesLoading ? "Loading…" : "Pick a workspace"} />
                </SelectTrigger>
                <SelectContent>
                  {(workspaces ?? []).map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.workspace_label ?? w.display_name ?? w.id}{" "}
                      <span className="text-muted-foreground">· {w.account_tier ?? "—"}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[180px]">
              <label className="text-xs text-muted-foreground">Window</label>
              <Select value={window} onValueChange={(v) => setWindow(v as WindowKey)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mtd">Month-to-date</SelectItem>
                  <SelectItem value="last_month">Last month</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[160px]">
              <label className="text-xs text-muted-foreground">Stripe env</label>
              <Select
                value={environment}
                onValueChange={(v) => setEnvironment(v as "sandbox" | "live")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sandbox">Sandbox (test)</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={() => refetch()} disabled={!workspaceId || isFetching}>
              <RefreshCcw className="h-4 w-4" /> Refresh
            </Button>
            <Button variant="outline" onClick={downloadCsv} disabled={!data}>
              <Download className="h-4 w-4" /> CSV
            </Button>
            <BaselineEditorButton onSaved={() => refetch()} />
          </div>
          <p className="text-xs text-muted-foreground">
            AI inference and Stripe revenue are real numbers. Storage, database, egress, and edge
            invocations are estimates prorated from the platform-cost baseline. Use{" "}
            <strong>Edit baseline</strong> to update the monthly Cloud bill numbers. Revenue is
            pulled from{" "}
            <strong>{environment === "live" ? "live" : "sandbox (test)"}</strong> Stripe.
          </p>
        </CardContent>
      </Card>

      {!workspaceId && (
        <p className="text-sm text-muted-foreground">Pick a workspace to see its cost breakdown.</p>
      )}

      {workspaceId && isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      )}

      {error && (
        <Card>
          <CardContent className="pt-6 text-sm text-destructive">
            {(error as Error).message}
          </CardContent>
        </Card>
      )}

      {data && <CostBreakdownCards data={data} environment={environment} />}
    </div>
  );
}
